import { classnames } from 'tailwindcss-classnames';
import { ImageField, RouteData, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import PlaceholderWrapper from 'components/helpers/PlaceholderWrapper/PlaceholderWrapper';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { Routes } from '.generated/templates/Project.Routes.model';
import { Data } from '.generated/templates/Feature.Data.model';
import useDictionary from 'lib/sitecore/use-dictionary';
import { LayoutPageSectionShell } from 'components/authorable/layout/LayoutPageSection/LayoutPageSection';
import { LayoutRightThirdShell } from 'components/authorable/layout/LayoutRightThird/LayoutRightThird';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { undefinedDate } from 'lib/util/event';
import getThemeClasses from 'lib/util/theming';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import ImageCaption from 'components/helpers/ImageCaption/ImageCaption';
import VideoItem, { VideoItemProps } from 'components/helpers/VideoItem/VideoItem';
import { formatDateShortMonth } from 'lib/util/date-formatters';

export type ArticlePageProps = Sitecore.Override.ComponentBase &
  RouteData &
  Routes.Fields.ArticlePage & {
    // eslint-disable-next-line react/no-unused-prop-types
    fields: {
      author?: Data.Authors.Fields.Author;
      pageFeaturedVideo?: VideoItemProps;
    };
  };

const ArticlePage = ({ rendering }: ArticlePageProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const dictionary = useDictionary();
  const { fields } = sitecoreContext.route as ArticlePageProps;
  if (!fields) {
    return <MissingDataSource />; // No fields? Exit early
  }
  const featuredImage = fields.pageFeaturedImage.value?.src
    ? fields.pageFeaturedImage
    : (sitecoreContext.defaultFeaturedImage as ImageField);
  const by = dictionary.By;
  let formattedPublishedDate = '';
  if (fields.publishedDate?.value && fields.publishedDate.value !== undefinedDate) {
    formattedPublishedDate = formatDateShortMonth(fields.publishedDate.value);
  }

  const prefix = fields.author?.fields.prefix.value
    ? fields.author?.fields.prefix.value.concat(' ')
    : '';

  const firstName = fields.author?.fields.firstName.value
    ? fields.author?.fields.firstName.value
    : '';

  const lastName = fields.author?.fields.lastName.value
    ? ' '.concat(fields.author?.fields.lastName.value)
    : '';

  const suffix = fields.author?.fields.suffix.value
    ? ' '.concat(fields.author?.fields.suffix.value)
    : '';

  const title = fields.author?.fields.title.value
    ? ', '.concat(fields.author?.fields.title.value)
    : '';

  const fullAuthor = `${prefix}${firstName}${lastName}${suffix}${title}`;

  const titleContainer = (
    <>
      <div
        className={classnames(
          'text-theme-date-text',
          'text-base',
          'leading-3',
          'font-semibold',
          'py-3'
        )}
      >
        {formattedPublishedDate}
      </div>
      <h1 className={classnames('text-theme-heading-text', 'heading-xl', 'pb-3')}>
        <Text field={fields.pageTitle} />
      </h1>
      {fields.author?.fields.firstName && (
        <div className={classnames('pb-6')}>
          {by} <strong>{fullAuthor}</strong>
        </div>
      )}
    </>
  );
  const leftColumn = (
    <div className={classnames('flex', 'flex-col')}>
      {!fields.pageFeaturedVideo ? (
        <div className={classnames('image-block')}>
          <ImageWrapper field={featuredImage} />
          <ImageCaption
            imageCaption={fields.featuredImageCaption}
            classNames={classnames('px-4')}
          />
        </div>
      ) : (
        <div
          className={classnames(
            'col-span-2',
            'md:col-span-1',
            'lg:col-span-2',
            'md:grid-cols-2',
            'lg:grid-cols-3'
          )}
        >
          <VideoItem {...fields.pageFeaturedVideo} />
        </div>
      )}
      <div className={classnames('block', 'sm:hidden')}>{titleContainer}</div>
    </div>
  );

  return (
    <div
      data-component="authorable/pages/articlepage"
      className={classnames('page-section-gap-closer', getThemeClasses('theme-white'))}
    >
      <LayoutPageSectionShell theme="theme-inherit" padding="tiny">
        <div className={classnames('hidden', 'sm:block')}>{titleContainer}</div>
        <LayoutRightThirdShell
          left={leftColumn}
          right={<PlaceholderWrapper name="nmdp-one-third-right" rendering={rendering} />}
        />
      </LayoutPageSectionShell>
    </div>
  );
};

export default ArticlePage;
